// frame: CoKKd @ ui/dogdisk-agent.pen (推荐设置 高度 1200)
// spec: docs/ui/agent-page-flows.md §CoKKd + §pen-to-code-backlog.md §3.4 B5 CoKKd
//
// 推荐用户 UUID 白名单管理:
//   - 状态 A:白名单非空 → 内容按 content.updated_at DESC 倒序展示
//   - 状态 B:白名单空 → 回退到全局"最热"算法
//
// **数据源**:supabase.from('agent_setting').update({recommend_uuids, algorithm_mode});
// TODO(api): agent_setting 表当前有 rate_bps / level 字段,**未落 recommend_uuids / algorithm_mode 列** —
// 待 schema migration 加列后启用。
import { useState } from 'react'
import { Sparkles, Plus, BadgeCheck, Trash2, Save, AlertCircle, Users } from 'lucide-react'
import { Spinner, useToast } from '@dogdisk/shared'
import { AgentLayout } from '../components/agent-layout'
import { useRecommendSetting, useUpdateRecommendSetting } from '../_lib/agent-api'

const UUID_V4_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const MAX_WHITELIST = 50

export function AgentRecommendSettingsPage() {
  const toast = useToast()
  const { data, isLoading } = useRecommendSetting()
  const update = useUpdateRecommendSetting()
  const [uuids, setUuids] = useState<string[]>([])
  const [pendingInput, setPendingInput] = useState('')
  const [inlineError, setInlineError] = useState<string | null>(null)
  const [initialized, setInitialized] = useState(false)

  // 首次取到 server 数据 → 写入本地编辑 buffer
  if (!initialized && data) {
    setUuids(data.recommend_uuids ?? [])
    setInitialized(true)
  }

  const algoMode: 'A' | 'B' = uuids.length > 0 ? 'A' : 'B'

  const validate = (uuid: string): string | null => {
    if (!UUID_V4_RE.test(uuid)) return 'UUID v4 格式不合法'
    if (uuids.includes(uuid)) return '已添加'
    if (uuids.length >= MAX_WHITELIST) return `已达上限 ${MAX_WHITELIST} 位`
    return null
  }

  const onAdd = () => {
    setInlineError(null)
    const v = pendingInput.trim().toLowerCase()
    const err = validate(v)
    if (err) {
      setInlineError(err)
      return
    }
    setUuids([v, ...uuids])
    setPendingInput('')
  }

  const onRemove = (uuid: string) => {
    setUuids(uuids.filter((u) => u !== uuid))
  }

  const onSave = async () => {
    try {
      await update.mutateAsync({ recommend_uuids: uuids, algorithm_mode: algoMode })
      toast.push('推荐设置已保存', { variant: 'success' })
    } catch (e) {
      // TODO(api): agent_setting.recommend_uuids 列未落 → 真 update 会返 PostgREST schema error
      toast.push(
        e instanceof Error
          ? `${e.message}(注:recommend_uuids 列未落,保存暂不可用)`
          : '保存失败',
        { variant: 'error' },
      )
    }
  }

  return (
    <AgentLayout topbarTitle="代理 · 推荐设置">
      <div className="mx-auto flex w-[1080px] flex-col gap-6 px-6 py-8" data-testid="CoKKd">
        <header className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Sparkles size={22} className="text-amber-500" />
            <div>
              <h1 className="text-xl font-bold text-gray-900">推荐设置</h1>
              <p className="text-sm text-gray-500">
                配置推荐用户白名单(UUID v4) · 非空时按 content.updated_at 倒序;空时回退最热
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => void onSave()}
            disabled={update.isPending}
            className="inline-flex items-center gap-1.5 rounded-md bg-amber-500 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
            data-testid="save-recommend"
          >
            <Save size={14} />
            {update.isPending ? '保存中…' : '保存配置'}
          </button>
        </header>

        {/* 算法说明 — 双态对比卡 */}
        <section className="grid grid-cols-2 gap-4">
          <AlgoCard
            active={algoMode === 'A'}
            badge="状态 A"
            title="已配置 UUID 列表"
            desc="按推荐顺序展示;每个 UUID 命中的最新内容优先"
            flow={['2 分钟前', '14 分钟前', '1 小时前', '3 小时前']}
            foot="排序:内容更新时间倒序(content.updated_at DESC)"
            tone="amber"
          />
          <AlgoCard
            active={algoMode === 'B'}
            badge="状态 B"
            title="未配置 UUID(列表为空)"
            desc="自动回退到全局算法"
            flow={['白名单为空', '最热 算法', '全局默认']}
            foot="无白名单 → 平台兜底"
            tone="gray"
          />
        </section>

        {/* 主表 */}
        <section className="rounded-xl border border-gray-200 bg-white">
          <header className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
            <div className="flex items-center gap-2">
              <Users size={18} className="text-gray-500" />
              <h2 className="text-sm font-semibold text-gray-900">推荐用户 UUID 列表</h2>
              <span className="rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                {uuids.length} / {MAX_WHITELIST}
              </span>
            </div>
          </header>

          {/* input row */}
          <div className="flex items-center gap-2 border-b border-gray-100 px-5 py-3">
            <input
              type="text"
              value={pendingInput}
              onChange={(e) => {
                setPendingInput(e.target.value)
                setInlineError(null)
              }}
              placeholder="Paste user UUID, e.g. 9f3c1a08-72d4-4a35-b3c2-0e7c1a5f3b22"
              className="h-9 flex-1 rounded-md border border-gray-300 bg-white px-3 font-mono text-xs"
              data-testid="uuid-input"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  onAdd()
                }
              }}
            />
            <button
              type="button"
              onClick={onAdd}
              disabled={!pendingInput.trim()}
              className="inline-flex h-9 items-center gap-1 rounded-md border border-gray-300 px-3 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
              data-testid="check-uuid"
            >
              <BadgeCheck size={14} />
              校验
            </button>
            <button
              type="button"
              onClick={onAdd}
              disabled={!pendingInput.trim() || uuids.length >= MAX_WHITELIST}
              className="inline-flex h-9 items-center gap-1 rounded-md bg-amber-500 px-3 text-xs font-semibold text-white disabled:opacity-60"
              data-testid="add-uuid"
            >
              <Plus size={14} />
              添加
            </button>
          </div>

          {inlineError && (
            <p className="border-b border-gray-100 bg-red-50 px-5 py-2 text-xs text-red-700">
              {inlineError}
            </p>
          )}

          {/* 列表 */}
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner className="text-amber-500" />
            </div>
          ) : uuids.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-12 text-sm text-gray-400">
              <AlertCircle size={18} />
              <p>白名单为空 — 当前走"最热"全局算法</p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {uuids.map((u) => (
                <li
                  key={u}
                  className="flex items-center justify-between px-5 py-3"
                  data-testid={`uuid-row-${u}`}
                >
                  <code className="truncate font-mono text-xs text-gray-700">{u}</code>
                  <button
                    type="button"
                    onClick={() => onRemove(u)}
                    className="rounded p-1 text-red-500 hover:bg-red-50"
                    aria-label="移除"
                    data-testid={`remove-uuid-${u}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </AgentLayout>
  )
}

function AlgoCard({
  active,
  badge,
  title,
  desc,
  flow,
  foot,
  tone,
}: {
  active: boolean
  badge: string
  title: string
  desc: string
  flow: string[]
  foot: string
  tone: 'amber' | 'gray'
}) {
  const bg = tone === 'amber' ? 'bg-amber-50' : 'bg-gray-50'
  const ring = active ? (tone === 'amber' ? 'ring-2 ring-amber-400' : 'ring-2 ring-gray-400') : ''
  return (
    <div className={`rounded-xl border border-gray-200 p-4 ${bg} ${ring}`}>
      <span
        className={`inline-block rounded px-2 py-0.5 text-[11px] font-medium ${
          tone === 'amber' ? 'bg-amber-200 text-amber-900' : 'bg-gray-200 text-gray-700'
        }`}
      >
        {badge}
        {active && ' · 当前'}
      </span>
      <h3 className="mt-2 text-sm font-semibold text-gray-900">{title}</h3>
      <p className="mt-1 text-xs text-gray-500">{desc}</p>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {flow.map((f, i) => (
          <span
            key={i}
            className="rounded bg-white px-2 py-1 text-[10px] text-gray-600 ring-1 ring-gray-200"
          >
            {f}
          </span>
        ))}
      </div>
      <p className="mt-3 text-[11px] text-gray-400">{foot}</p>
    </div>
  )
}
